import type { TOC } from '@ember/component/template-only';
import { formatNumber } from '../../utils/charts/chart-layout.ts';
import type { ChartDataPoint } from '../../types/charts.ts';

interface SummaryDisplaySignature {
  Args: {
    /** Chart data points — values are summed for display */
    data: ChartDataPoint[];
    /** Label below the value (default: "Total") */
    label?: string;
    /** Text color class (default: tio-text-default) */
    valueColor?: string;
    /** Compact mode for dashboard tiles */
    compact?: boolean;
    /** Optional value formatter (e.g. currency / divisor); defaults to formatNumber. */
    formatValue?: (value: number) => string;
  };
  Element: HTMLDivElement;
}

const getTotal = (data: ChartDataPoint[]) => data.reduce((sum, d) => sum + d.value, 0);

const getFormattedTotal = (data: ChartDataPoint[], formatValue?: (value: number) => string) => {
  const total = getTotal(data);
  return formatValue ? formatValue(total) : formatNumber(total);
};

const getLabel = (label?: string) => label ?? 'Total';

const getValueColor = (color?: string) => color ?? 'tio-text-default';

const SummaryDisplay: TOC<SummaryDisplaySignature> = <template>
  <div
    data-test-chart
    data-test-chart-type="summary"
    class="tio-place-center text-center {{if @compact 'py-3 px-3' 'py-6 px-4'}}"
    ...attributes
  >
    <span
      data-test-chart-value
      class="font-bold leading-none break-words
        {{if @compact 'text-2xl sm:text-3xl' 'text-4xl sm:text-5xl lg:text-6xl'}}
        {{getValueColor @valueColor}}"
    >
      {{getFormattedTotal @data @formatValue}}
    </span>
    {{! An explicit empty label ("") hides the line; undefined still ⇒ "Total". }}
    {{#if (getLabel @label)}}
      <span
        class="tio-text-muted max-w-full break-words {{if @compact 'mt-1 text-xs' 'mt-2 text-sm'}}"
      >
        {{getLabel @label}}
      </span>
    {{/if}}
  </div>
</template>;

export default SummaryDisplay;
export type { SummaryDisplaySignature };
